/**
 * Meta Graph API configuration for the v2 dashboard.
 *
 * NOTE: these read `VITE_*` env vars, which Vite inlines into the browser
 * bundle — the access token is therefore visible to anyone who can open the
 * app's devtools. v1 deliberately moved its two Meta calls behind the backend
 * proxy (`/v1/data-analytics/meta/*`) for that reason, but that proxy only
 * exposes `campaigns` + `insights`; there is no ad set / ad edge yet. See
 * `API.md` in this folder for the contract the backend needs to expose; once it
 * exists, swap `fetchAllPages` to go through `dataAnalyticsApi` and delete
 * `META_ACCESS_TOKEN` / `META_AD_ACCOUNT_ID` from here.
 */
const API_VERSION = import.meta.env.VITE_META_API_VERSION || 'v21.0';

export const META_GRAPH_BASE_URL = `https://graph.facebook.com/${API_VERSION}`;

const RAW_AD_ACCOUNT_ID: string = import.meta.env.VITE_META_AD_ACCOUNT_ID ?? '';

/** Ad account id, auto-prefixed with `act_` the way the Graph API expects. */
export const META_AD_ACCOUNT_ID = RAW_AD_ACCOUNT_ID.startsWith('act_')
  ? RAW_AD_ACCOUNT_ID
  : `act_${RAW_AD_ACCOUNT_ID}`;

export const META_ACCESS_TOKEN: string =
  import.meta.env.VITE_META_ACCESS_TOKEN ?? '';

/** Gate the fetch so a missing token surfaces as a message, not a 400 from Meta. */
export const HAS_META_CREDENTIALS = Boolean(
  META_ACCESS_TOKEN && RAW_AD_ACCOUNT_ID,
);

/**
 * Fields per edge. Ad sets and ads carry their parent ids so the tree can be
 * assembled client-side without nested field expansion (which silently
 * truncates children at the nested `limit`).
 */
export const EDGE_FIELDS = {
  campaigns: 'id,name,effective_status',
  adsets: 'id,name,effective_status,campaign_id',
  ads: 'id,name,effective_status,adset_id,campaign_id',
} as const;

/** One entry in Meta's `filtering` param. */
export interface MetaGraphFilter {
  field: string;
  operator: string;
  value: string[];
}

/**
 * Restricts the campaigns sweep to live campaigns. This is the single biggest
 * performance lever on this account: unfiltered, the three edges return ~3,300
 * objects over 18 cursor pages (~10s); scoped to active campaigns they return
 * ~40 objects over 3 requests (~1.7s). 2,711 of the account's 2,728 ads sit
 * under paused campaigns.
 *
 * Note this filters CAMPAIGNS only — ad sets and ads inside an active campaign
 * are returned whatever their own status, so paused children still show up (in
 * red).
 */
export const ACTIVE_CAMPAIGNS_FILTER: MetaGraphFilter[] = [
  { field: 'effective_status', operator: 'IN', value: ['ACTIVE'] },
];

/**
 * Rows per Graph page. 500 is the edge maximum; it cuts the full-account ads
 * sweep from 14 pages to 6 (cursor pages are strictly serial, so page count is
 * wall-clock).
 */
export const GRAPH_PAGE_LIMIT = 500;

/** Hard stop on cursor-following so a bad `paging.next` can't loop forever. */
export const MAX_GRAPH_PAGES = 25;

/**
 * Campaign ids per `campaign.id IN [...]` child sweep. Keeps the query string
 * well under Graph's URL limit if the account ever has hundreds of live
 * campaigns; chunks are fetched in parallel.
 */
export const CAMPAIGN_ID_CHUNK_SIZE = 50;

/** The one `effective_status` that counts as "alive" (green dot). */
export const ACTIVE_EFFECTIVE_STATUS = 'ACTIVE';

// --- insights ---------------------------------------------------------------

/**
 * Insights fields, each verified as actually populated on this ad account
 * (probed 2026-08-05, level=ad, last 5 weeks, 234 rows).
 *
 * Deliberately NOT requested — Meta returned them for zero rows because this is
 * a lead-gen account with no purchase/value events: `action_values`,
 * `conversion_values`, `purchase_roas`.
 */
export const INSIGHTS_FIELDS = [
  'spend',
  'impressions',
  'reach',
  'frequency',
  'clicks',
  'unique_clicks',
  'ctr',
  'unique_ctr',
  'cpc',
  'cpm',
  'cpp',
  'inline_link_clicks',
  'inline_link_click_ctr',
  'cost_per_inline_link_click',
  'unique_inline_link_clicks',
  'actions',
  // Custom-conversion counts (MQL) live here, NOT in `actions` — `actions` only
  // carries the aggregate `offsite_conversion.fb_pixel_custom`.
  'conversions',
  'account_currency',
  'date_start',
  'date_stop',
].join(',');

/**
 * How many ISO weeks the grid shows, ending with the current (partial) week.
 * One additional week is always fetched but never displayed — it's the baseline
 * the leftmost visible column measures its change against.
 */
export const WEEKS_SHOWN = 5;

/**
 * `action_type` keys unpacked from the insights `actions` array. Verified
 * present on this account; they are pixel/setup-specific, so re-check these if
 * the numbers ever read zero.
 */
export const LEAD_ACTION_TYPE = 'offsite_conversion.fb_pixel_lead';
export const LANDING_PAGE_VIEW_ACTION_TYPE = 'landing_page_view';
export const POST_ENGAGEMENT_ACTION_TYPE = 'post_engagement';
export const VIDEO_VIEW_ACTION_TYPE = 'video_view';

/**
 * Custom conversions counted as a marketing qualified lead, read from the
 * insights `conversions` array and summed.
 *
 * The account has the MQL setup under TWO custom conversions, both still firing
 * (30 days to 2026-08-11: `marketingqualifiedlead` 102, `Marketing Qualified`
 * 41). They are summed here on the assumption that they are separate events.
 *
 * CAVEAT — if the two custom conversions overlap (both firing on one pixel
 * event), this double counts, and the Meta API gives no way to tell from the
 * outside. Confirming that in Events Manager is the only fix; dropping either
 * string from this array is the one-line change if they do overlap.
 */
export const MQL_CONVERSION_ACTION_TYPES = [
  'offsite_conversion.fb_pixel_custom.marketingqualifiedlead',
  'offsite_conversion.fb_pixel_custom.Marketing Qualified',
] as const;

// --- currency ---------------------------------------------------------------

/**
 * The currency Meta bills this ad account in — every money field in an insights
 * response is denominated in it. Confirmed live: `account_currency` is `HKD`.
 */
export const ACCOUNT_CURRENCY = 'HKD';

/**
 * Fixed-peg fallback, HKD per 1 USD. HKD is pegged to USD (~7.75–7.85), so this
 * is accurate to well under 1% whenever the live rate is unavailable.
 */
export const HKD_PER_USD = 7.8;

/**
 * Ceiling on ids in a single `IN [...]` scope filter. Past this the query
 * string gets unreasonable, so the grid asks for a narrower selection rather
 * than silently truncating.
 */
export const MAX_SCOPE_IDS = 300;
